import type { HttpInterceptorFn } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { AppNotificationService } from './app-notification.service';

interface RequestTracker {
  url: string;
  timestamp: number;
  count: number;
}

// Track requests globally across all instances
const requestHistory = new Map<string, RequestTracker>();

// Configuration
const MAX_REQUESTS_PER_URL = 5; // Maximum requests to same URL (reduced from 10)
const TIME_WINDOW_MS = 3000; // Within 3 seconds (reduced from 4)
const CLEANUP_INTERVAL_MS = 5000; // Clean up old entries every 5 seconds

// Periodic cleanup of old entries
setInterval(() => {
  const now = Date.now();
  for (const [url, tracker] of requestHistory.entries()) {
    if (now - tracker.timestamp > TIME_WINDOW_MS) {
      requestHistory.delete(url);
    }
  }
}, CLEANUP_INTERVAL_MS);

export const infiniteLoopGuardInterceptor: HttpInterceptorFn = (req, next) => {
  const notification = inject(AppNotificationService);
  const url = req.urlWithParams;
  const now = Date.now();

  // Get or create tracker for this URL
  let tracker = requestHistory.get(url);

  if (!tracker) {
    // First request to this URL
    tracker = { url, timestamp: now, count: 1 };
    requestHistory.set(url, tracker);
  } else {
    // Check if within time window
    const timeSinceFirst = now - tracker.timestamp;

    if (timeSinceFirst <= TIME_WINDOW_MS) {
      // Still within time window, increment count
      tracker.count++;

      if (tracker.count > MAX_REQUESTS_PER_URL) {
        // Infinite loop detected!
        console.error(
          `[Infinite Loop Detected] Blocked request to ${url}. ` +
            `${tracker.count} requests within ${timeSinceFirst}ms`
        );

        // Show error notification that requires user dismissal (duration: 0)
        notification.showErrorMessage(
          `Too many rapid requests detected (${tracker.count} in ${Math.round(timeSinceFirst / 1000)}s). ` +
            `Request to "${url}" blocked to prevent server overload. ` +
            `This may indicate an infinite loop in the application.`
        );

        // Reset tracker to allow requests again after cooldown
        requestHistory.delete(url);        // Return error observable
        return throwError(() => new HttpErrorResponse({
          error: 'Infinite loop detected - request blocked by client',
          status: 429, // Too Many Requests
          statusText: 'Too Many Requests (Client-side protection)',
          url: url
        }));
      }
    } else {
      // Outside time window, reset tracker
      tracker.timestamp = now;
      tracker.count = 1;
    }
  }

  // Allow request to proceed
  return next(req);
};
